import { RefObject, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import { AUTH_ROUTES } from '@/constants/constants';
import { ForbiddenError, UnauthorizedError, ValidationError } from '@/utils/errors';

type DetailError = {
    loc: string[];
    msg: string;
    type: string;
    field?: string;
};

export interface ErrorMessage {
    message?: string;
    errors?: DetailError[];
    field?: string;
}

export function useHandleError(scrollTargetRef?: RefObject<HTMLElement | null>) {
    const router = useRouter();
    const pathname = usePathname();
    const { closeModal } = useModalStore();
    const { closeAllModalSlides } = useModalSlideStore();
    const [error, setError] = useState<Error | null>(null);
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
    const [stringErrorMessage, setStringErrorMessage] = useState<string | null>(null);
    const { showToastAsync } = useToastStore();

    async function handleError(err: unknown) {
        const scrollToError = () => {
            scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };

        if (err instanceof ValidationError) {
            try {
                const parsed = JSON.parse(err.message);
                if (Array.isArray(parsed)) {
                    setErrorMessage({
                        errors: parsed.map((e) => ({
                            ...e,
                            field: e.loc?.[e.loc.length - 1] || '',
                        })),
                    });
                } else {
                    setErrorMessage({ message: err.message });
                }
            } catch {
                setErrorMessage({ message: err.message });
            }
            scrollToError();
            return;
        }

        if (err instanceof UnauthorizedError) {
            if (AUTH_ROUTES.includes(pathname)) {
                router.push('/auth/login');
                return;
            }
        }

        if (err instanceof ForbiddenError) {
            closeModal();
            closeAllModalSlides();
            router.push('/error/forbidden');
            return;
        }

        const isDev = process.env.NODE_ENV === 'development';
        if (isDev) {
            console.error(err);
        }
        showToastAsync('ネットワークが不安定なため、時間をおいて再度お試しください', 'systemError', 5000);
    }

    function clearError() {
        setError(null);
        setErrorMessage(null);
        setStringErrorMessage(null);
    }

    useEffect(() => {
        if (error) throw error;
    }, [error]);

    return {
        handleError,
        errorMessage,
        stringErrorMessage,
        clearError,
        setErrorMessage,
        setStringErrorMessage,
    };
}
